/**
 * Native gzip helpers built on the Compression Streams API
 * (CompressionStream / DecompressionStream — Chrome 80+, Safari 16.4+,
 * Firefox 113+). Shrinks heavy IndexedDB payloads before they're persisted.
 *
 * Degrades gracefully:
 *   - API unavailable → return null → caller stores raw
 *   - Payload too small → return null (gzip framing overhead not worth it)
 *   - Compressed form larger → return null
 */

const HAS_CS =
  typeof CompressionStream !== 'undefined' &&
  typeof DecompressionStream !== 'undefined'

/** Skip gzip when JSON is under 512 bytes — header overhead wins. */
const MIN_BYTES = 512

async function deflate(text: string): Promise<Uint8Array> {
  const stream = new Blob([text])
    .stream()
    .pipeThrough(new CompressionStream('gzip'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/**
 * Compress a JSON-serialisable value. Returns null when compression
 * wouldn't help (missing API, small payload, or no net win).
 */
export async function maybeGzipJson(value: unknown): Promise<Uint8Array | null> {
  if (!HAS_CS) return null
  let text: string
  try {
    text = JSON.stringify(value)
  } catch {
    return null
  }
  if (!text || text.length < MIN_BYTES) return null
  try {
    const bytes = await deflate(text)
    return bytes.byteLength < text.length ? bytes : null
  } catch {
    return null
  }
}

/** Inflate gzipped bytes back to the original value. */
export async function gunzipJson<T>(bytes: Uint8Array): Promise<T> {
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'))
  const text = await new Response(stream).text()
  return JSON.parse(text) as T
}
